<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, defineProps, defineEmits, defineExpose } from "vue";
import { useRouter } from 'vue-router';
import Lottie from "@/components/widgets/lottie.vue";
import Swal from "sweetalert2";
import profileService from "@/services/userProfile-service";

const props = defineProps({
    timeout: {
        type: Number,
        default: 300000,
        validator: (value) => value >= 10000
    },
    countdownDuration: {
        type: Number,
        default: 120000,
        validator: (value) => value >= 10000
    },
    message: {
        type: String,
        default: "Session verrouillée"
    },
    subMessage: {
        type: String,
        default: "Votre session a été verrouillée par mesure de sécurité"
    },
    showCountdown: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['locked', 'unlocked', 'countdown-tick']);

const router = useRouter();
const isLocked = ref(false);
const showUnlockForm = ref(false);
const countdown = ref(0);
const inactivityTimer = ref(null);
const countdownInterval = ref(null);
const formInactivityTimer = ref(null);
const lastActivity = ref(Date.now());
const navigationHandler = ref(null);

// Données du formulaire de déverrouillage
const user = ref(JSON.parse(localStorage.getItem("user")) || null);
const password = ref("");
const loading = ref(false);
const errorMessage = ref("");
const showPassword = ref(false);

// Événements à surveiller
const activityEvents = [
    'mousemove', 'mousedown', 'click', 'scroll',
    'keypress', 'keydown', 'keyup',
    'touchstart', 'touchmove', 'touchend'
];

// Vérifier si la route courante doit avoir le lockscreen
const shouldHaveLockscreen = computed(() => {
    const currentRoute = router.currentRoute.value;
    return !currentRoute.meta?.noLockscreen;
});

// Condition d'affichage finale
const shouldShowLockScreen = computed(() => {
    return isLocked.value && shouldHaveLockscreen.value;
});

// Formatage du compte à rebours
const formattedCountdown = computed(() => {
    const minutes = Math.floor(countdown.value / 60000);
    const seconds = Math.floor((countdown.value % 60000) / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

// Génère les initiales
const getInitials = (u) => {
    if (!u) return "U";
    const first = (u.firstname || u.username || "").charAt(0).toUpperCase();
    const last = (u.lastname || "").charAt(0).toUpperCase();
    return `${first}${last}` || first;
};

// Empêcher la navigation
function preventNavigation(event) {
    if (isLocked.value && shouldHaveLockscreen.value) {
        event.preventDefault();
        event.returnValue = 'Votre session est verrouillée. Déverrouillez-la avant de quitter.';
        return 'Votre session est verrouillée. Déverrouillez-la avant de quitter.';
    }
}

// Empêcher les changements manuels d'URL
function preventUrlChanges() {
    if (isLocked.value && shouldHaveLockscreen.value) {
        const currentUrl = window.location.href;
        setTimeout(() => {
            if (window.location.href !== currentUrl) {
                window.history.pushState(null, "", currentUrl);
                console.log("Changement d'URL bloqué - écran verrouillé");
            }
        }, 0);
    }
}

// Empêcher toute interaction avec la page derrière
function preventBackgroundInteraction(event) {
    if (isLocked.value && shouldHaveLockscreen.value && !event.target.closest('.lockscreen-modal')) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }
}

// Blocage des interactions clavier avec la page background
function preventBackgroundKeyboard(event) {
    if (isLocked.value && shouldHaveLockscreen.value && !showUnlockForm.value) {
        const allowedKeys = ['Escape', 'Tab', 'Enter'];
        if (!allowedKeys.includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
        }
    }
}

// ===== NOUVEAU : Réinitialiser complètement avec les nouvelles durées =====
function resetWithNewSettings(timeoutMs, countdownMs) {
    console.log('🟢 Lockscreen reçoit nouvelles durées:', { timeoutMs, countdownMs });

    // Arrêter tous les timers
    clearTimeout(inactivityTimer.value);
    clearInterval(countdownInterval.value);
    clearTimeout(formInactivityTimer.value);

    // Si l'écran est verrouillé, mettre à jour le countdown
    if (isLocked.value) {
        countdown.value = countdownMs;

        if (props.showCountdown) {
            countdownInterval.value = setInterval(() => {
                countdown.value -= 1000;
                emit('countdown-tick', countdown.value);

                if (countdown.value <= 0) {
                    forceLogout();
                }
            }, 1000);
        }
    } else {
        // Si non verrouillé, redémarrer le timer d'inactivité avec la nouvelle durée
        resetTimer();
    }

    console.log('Lockscreen mis à jour avec les nouvelles durées');
}

// Réinitialiser le timer d'inactivité
function resetTimer() {
    // Ne pas reset le timer si on est sur une page sans lockscreen
    if (!shouldHaveLockscreen.value) return;

    lastActivity.value = Date.now();
    clearTimeout(inactivityTimer.value);

    inactivityTimer.value = setTimeout(() => {
        lockScreen();
    }, props.timeout);

    console.log('Timer d\'inactivité réinitialisé avec timeout:', props.timeout);
}

// Timer d'inactivité pour le formulaire (30 secondes)
function resetFormTimer() {
    clearTimeout(formInactivityTimer.value);
    formInactivityTimer.value = setTimeout(() => {
        backToLockScreen();
    }, 30000);
}

// Verrouiller l'écran
function lockScreen() {
    if (isLocked.value || !shouldHaveLockscreen.value) {
        console.log("Lockscreen désactivé pour cette page:", router.currentRoute.value.path);
        return;
    }

    console.log("Verrouillage de l'écran");
    isLocked.value = true;
    showUnlockForm.value = false;
    countdown.value = props.countdownDuration;

    // EMPÊCHER LA NAVIGATION ET LES INTERACTIONS
    window.addEventListener('beforeunload', preventNavigation);
    window.addEventListener('popstate', preventUrlChanges);
    window.addEventListener('hashchange', preventUrlChanges);

    // Empêcher les interactions avec le background
    document.addEventListener('click', preventBackgroundInteraction, true);
    document.addEventListener('mousedown', preventBackgroundInteraction, true);
    document.addEventListener('keydown', preventBackgroundKeyboard, true);
    document.addEventListener('submit', preventBackgroundInteraction, true);

    // Bloquer les touches d'actualisation
    navigationHandler.value = (e) => {
        if ((e.ctrlKey && e.key === 'r') ||
            (e.key === 'F5') ||
            (e.metaKey && e.key === 'r') ||
            (e.ctrlKey && e.key === 'F5')) {
            e.preventDefault();
            e.stopPropagation();
            console.log("Actualisation bloquée");
            return false;
        }
    };
    window.addEventListener('keydown', navigationHandler.value, true);

    // Désactiver le menu contextuel
    document.addEventListener('contextmenu', preventBackgroundInteraction, true);

    if (props.showCountdown) {
        countdownInterval.value = setInterval(() => {
            countdown.value -= 1000;
            emit('countdown-tick', countdown.value);

            if (countdown.value <= 0) {
                forceLogout();
            }
        }, 1000);
    }

    emit('locked');
    localStorage.setItem("isLocked", "true");
}

// Afficher le formulaire de déverrouillage
function showUnlock() {
    console.log("Affichage du formulaire de déverrouillage");
    showUnlockForm.value = true;
    clearInterval(countdownInterval.value);
    resetFormTimer();
    resetForm();
}

// Réinitialiser le formulaire
function resetForm() {
    password.value = "";
    errorMessage.value = "";
    loading.value = false;
    showPassword.value = false;
}

// Retour à l'écran principal de verrouillage
function backToLockScreen() {
    console.log("Retour à l'écran de verrouillage");
    showUnlockForm.value = false;
    resetForm();
    clearTimeout(formInactivityTimer.value);

    if (isLocked.value && props.showCountdown) {
        countdownInterval.value = setInterval(() => {
            countdown.value -= 1000;
            emit('countdown-tick', countdown.value);

            if (countdown.value <= 0) {
                forceLogout();
            }
        }, 1000);
    }
}

// Déverrouiller l'écran
function unlock() {
    console.log("Déverrouillage de l'écran");
    isLocked.value = false;
    showUnlockForm.value = false;
    clearInterval(countdownInterval.value);
    clearTimeout(formInactivityTimer.value);
    countdown.value = 0;

    // RÉACTIVER LA NAVIGATION ET LES INTERACTIONS
    window.removeEventListener('beforeunload', preventNavigation);
    window.removeEventListener('popstate', preventUrlChanges);
    window.removeEventListener('hashchange', preventUrlChanges);

    document.removeEventListener('click', preventBackgroundInteraction, true);
    document.removeEventListener('mousedown', preventBackgroundInteraction, true);
    document.removeEventListener('keydown', preventBackgroundKeyboard, true);
    document.removeEventListener('submit', preventBackgroundInteraction, true);
    document.removeEventListener('contextmenu', preventBackgroundInteraction, true);

    if (navigationHandler.value) {
        window.removeEventListener('keydown', navigationHandler.value, true);
        navigationHandler.value = null;
    }

    resetTimer();
    resetForm();
    emit('unlocked');
    localStorage.setItem("isLocked", "false");
}

// Déconnexion forcée
function forceLogout() {
    console.log("Déconnexion forcée");
    clearInterval(countdownInterval.value);
    clearTimeout(formInactivityTimer.value);

    window.removeEventListener('beforeunload', preventNavigation);
    window.removeEventListener('popstate', preventUrlChanges);
    window.removeEventListener('hashchange', preventUrlChanges);

    document.removeEventListener('click', preventBackgroundInteraction, true);
    document.removeEventListener('mousedown', preventBackgroundInteraction, true);
    document.removeEventListener('keydown', preventBackgroundKeyboard, true);
    document.removeEventListener('submit', preventBackgroundInteraction, true);
    document.removeEventListener('contextmenu', preventBackgroundInteraction, true);

    if (navigationHandler.value) {
        window.removeEventListener('keydown', navigationHandler.value, true);
        navigationHandler.value = null;
    }

    localStorage.setItem("isLocked", "false");
    router.push("/logout");
}

// Gestionnaire d'événements d'activité
function handleActivity() {
    if (!isLocked.value && shouldHaveLockscreen.value) {
        resetTimer();
    }

    if (showUnlockForm.value) {
        resetFormTimer();
    }
}

// Déverrouillage via formulaire
const handleUnlock = async () => {
    if (!password.value.trim()) {
        errorMessage.value = "Veuillez entrer votre mot de passe";
        return;
    }

    loading.value = true;
    errorMessage.value = "";

    try {
        const response = await profileService.login({
            username: user.value.username,
            password: password.value
        });

        if (response.data?.success) {
            const { token, refresh_token, user: userData } = response.data.data;

            localStorage.setItem("token", token);
            localStorage.setItem("refresh_token", refresh_token);
            localStorage.setItem("isLocked", "false");

            if (userData) {
                localStorage.setItem("user", JSON.stringify(userData));
                user.value = userData;
            }

            await Swal.fire({
                title: "Déverrouillé !",
                text: "Bon retour !",
                icon: "success",
                confirmButtonColor: "#0ab39c",
                timer: 1500,
                showConfirmButton: false,
                customClass: {
                    container: 'swal-overlay-high-zindex'
                }
            });

            unlock();
        } else {
            throw new Error(response.data?.message || "Échec du déverrouillage");
        }
    } catch (error) {
        console.error("Erreur déverrouillage:", error);

        let errorMsg = "Mot de passe incorrect ou erreur de connexion";
        if (error.response?.data) {
            errorMsg = error.response.data.message || error.response.data.error_description || errorMsg;
        } else if (error.message) {
            errorMsg = error.message;
        }

        errorMessage.value = errorMsg;

        await Swal.fire({
            title: "Erreur",
            text: errorMsg,
            icon: "error",
            confirmButtonColor: "#f06548",
            timer: 1500,
            showConfirmButton: false,
            customClass: {
                container: 'swal-overlay-high-zindex'
            }
        });
    } finally {
        loading.value = false;
    }
};

// Redirection login
const login = async () => {
    localStorage.setItem("isLocked", "false");
    router.push("/login");
};

// Surveiller les changements de route
watch(() => router.currentRoute.value, (newRoute, oldRoute) => {
    console.log("Changement de route:", oldRoute?.path, "->", newRoute.path);

    // Déverrouiller automatiquement si on navigue vers une page exclue
    if (newRoute.meta?.noLockscreen && isLocked.value) {
        console.log("Déverrouillage automatique - page exclue:", newRoute.path);
        unlock();
    }

    // Reset du timer sur les pages avec lockscreen
    if (!newRoute.meta?.noLockscreen && !isLocked.value) {
        resetTimer();
    }
});

// Surveiller les changements de timeout
watch(() => props.timeout, (newTimeout, oldTimeout) => {
    console.log(`Timeout changé: ${oldTimeout}ms -> ${newTimeout}ms`);
    if (!isLocked.value && shouldHaveLockscreen.value) {
        resetTimer();
    }
});

// Surveiller les changements de countdown
watch(() => props.countdownDuration, (newCountdown, oldCountdown) => {
    console.log(`Countdown changé: ${oldCountdown}ms -> ${newCountdown}ms`);
    if (isLocked.value) {
        countdown.value = newCountdown;
    }
});

// Configuration initiale
onMounted(() => {
    // Vérifier si l'écran était déjà verrouillé
    const wasLocked = localStorage.getItem("isLocked") === "true";
    if (wasLocked && shouldHaveLockscreen.value) {
        isLocked.value = true;
        lockScreen();
    }

    activityEvents.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
    });

    if (shouldHaveLockscreen.value) {
        resetTimer();
    }

    window.addEventListener('keydown', (e) => {
        if ((e.ctrlKey && e.shiftKey && e.key === 'I') ||
            (e.key === 'F12') ||
            (e.ctrlKey && e.key === 'u')) {
            e.preventDefault();
            if (shouldHaveLockscreen.value) {
                lockScreen();
            }
        }
    });
});

// Nettoyage
onBeforeUnmount(() => {
    activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
    });

    clearTimeout(inactivityTimer.value);
    clearInterval(countdownInterval.value);
    clearTimeout(formInactivityTimer.value);

    window.removeEventListener('beforeunload', preventNavigation);
    window.removeEventListener('popstate', preventUrlChanges);
    window.removeEventListener('hashchange', preventUrlChanges);

    document.removeEventListener('click', preventBackgroundInteraction, true);
    document.removeEventListener('mousedown', preventBackgroundInteraction, true);
    document.removeEventListener('keydown', preventBackgroundKeyboard, true);
    document.removeEventListener('submit', preventBackgroundInteraction, true);
    document.removeEventListener('contextmenu', preventBackgroundInteraction, true);

    if (navigationHandler.value) {
        window.removeEventListener('keydown', navigationHandler.value, true);
    }
});

// Exposer les méthodes pour une utilisation externe
defineExpose({
    lock: lockScreen,
    unlock,
    forceLogout,
    reset: resetTimer,
    resetWithNewSettings // 👈 AJOUT IMPORTANT
});
</script>

<template>
    <div v-if="shouldShowLockScreen" class="lockscreen-overlay">
        <!-- Écran principal de verrouillage -->
        <div v-if="!showUnlockForm" class="lockscreen-modal p-4 rounded shadow bg-blur">
            <!-- En-tête -->
            <div class="text-center mb-5">
                <Lottie :options="{
                    loop: true,
                    autoplay: true,
                    animationData: require('@/assets/lockscreen.json'),
                    rendererSettings: {
                        preserveAspectRatio: 'xMidYMid slice'
                    }
                }" :height="100" :width="100" />
                <br>
                <br>
                <h3 class="fs-4 fw-semibold text-white mb-2">{{ message }}</h3>
                <p class="modal-subtitle text-muted">{{ subMessage }}</p>
            </div>

            <!-- Compte à rebours -->
            <div v-if="showCountdown && countdown <= 120000" class="countdown-section">
                <div class="countdown-display">
                    <div class="countdown-circle">
                        <span class="countdown-text">{{ formattedCountdown }}</span>
                    </div>
                </div>
                <div class="countdown-info">
                    <small class="text-muted">
                        Déconnexion automatique dans
                        <strong>{{ formattedCountdown }}</strong>
                    </small>
                </div>
            </div>

            <!-- Actions -->
            <div class="modal-actions">
                <BButton variant="primary" size="md" class="unlock-btn" @click="showUnlock" :disabled="countdown <= 0">
                    <i class="ri-lock-unlock-line me-2"></i>
                    Reprendre la session
                </BButton>

                <BButton v-if="showCountdown && countdown > 0" variant="outline-secondary" size="sm" class="logout-btn"
                    @click="forceLogout">
                    <i class="ri-logout-box-r-line me-2"></i>
                    Se déconnecter
                </BButton>
                <BButton v-if="showCountdown && countdown <= 0" variant="outline-secondary" size="sm" class="logout-btn"
                    @click="forceLogout">
                    <i class="ri-logout-box-r-line me-2"></i>
                    Se reconnecter
                </BButton>
            </div>

            <!-- Note de sécurité -->
            <div v-if="showCountdown && countdown > 0" class="security-note">
                <i class="ri-shield-check-line me-1"></i>
                <small>Pour des raisons de sécurité, votre session sera automatiquement fermée après <strong>{{
                    Math.floor(countdownDuration / 60000)
                        }}</strong> minutes d'inactivité.</small>
            </div>

            <div v-if="showCountdown && countdown <= 0" class="security-note alert-note">
                <i class="ri-alarm-warning-line me-1"></i>
                <small><strong>Alerte de sécurité :</strong> Une tentative d'actualisation a été détectée pendant le
                    verrouillage. Pour protéger votre session, nous vous recommandons de vous reconnecter.</small>
            </div>
        </div>

        <!-- Formulaire de déverrouillage -->
        <div v-else class="lockscreen-modal p-4 rounded shadow bg-blur unlock-form-modal">
            <!-- En-tête République Centrafricaine -->
            <div class="text-center mb-4">
                <p class="mt-3 fs-15 fw-medium text-white">REPUBLIQUE CENTRAFRICAINE</p>
                <router-link to="#" class="d-inline-block auth-logo">
                    <img src="@/assets/images/mepci.svg" alt="Logo Dûnîa" height="70">
                </router-link>
                <p class="mt-2 fs-13 fw-medium text-white-50">Ministère de l'Économie, du Plan et de la Coopération
                    Internationale (MEPCI).</p>
            </div>

            <!-- Titre -->
            <div class="text-center mt-2 mb-4">
                <h5 class="text-primary fw-semibold">Écran Verrouillé</h5>
                <p class="text-muted mb-0">Entrez votre mot de passe pour déverrouiller</p>
            </div>

            <!-- Avatar + infos -->
            <div class="user-thumb text-center mb-4">
                <div class="mb-3">
                    <img v-if="user?.image" :src="user.image" alt="Avatar utilisateur"
                        class="rounded-circle img-thumbnail" style="width: 70px; height: 70px; object-fit: cover;" />
                    <div v-else
                        class="d-flex justify-content-center align-items-center rounded-circle bg-primary text-white fw-bold mx-auto img-thumbnail"
                        style="width: 70px; height: 70px; font-size: 22px;">
                        {{ getInitials(user) }}
                    </div>
                </div>
                <h6 class="mb-1 text-white fw-semibold">{{ user?.firstname?.split(' ')[0] }}</h6>
                <p class="text-muted mb-3">{{ user?.email || user?.username || "Utilisateur" }}</p>
            </div>

            <!-- Formulaire -->
            <div class="p-3">
                <BForm @submit.prevent="handleUnlock" novalidate="true">
                    <div class="mb-4">
                        <label class="form-label text-white fw-medium" for="userpassword">Mot de passe</label>
                        <div class="position-relative">
                            <BFormInput :type="showPassword ? 'text' : 'password'" v-model="password" id="userpassword"
                                placeholder="Entrez votre mot de passe" :disabled="loading" class="pe-5 form-control-lg"
                                @input="resetFormTimer" @focus="resetFormTimer" @keydown="resetFormTimer" />
                            <span class="position-absolute top-50 end-0 translate-middle-y me-3"
                                style="cursor: pointer; z-index: 5;"
                                @click="showPassword = !showPassword; resetFormTimer()">
                                <i class="ri-eye-fill text-muted fs-5" v-if="showPassword"></i>
                                <i class="ri-eye-off-fill text-muted fs-5" v-else></i>
                            </span>
                        </div>
                        <BFormInvalidFeedback v-if="errorMessage" class="d-block mt-2">
                            <i class="ri-error-warning-line me-1"></i>{{ errorMessage }}
                        </BFormInvalidFeedback>
                    </div>

                    <div class="mb-3 mt-4">
                        <BButton variant="primary" class="w-100 py-2 fw-semibold" type="submit"
                            :disabled="loading || !password">
                            <BSpinner small v-if="loading" class="me-2" />
                            <i v-else class="ri-lock-unlock-line me-2"></i>
                            {{ loading ? 'Déverrouillage...' : 'Déverrouiller' }}
                        </BButton>
                    </div>
                </BForm>
            </div>

            <!-- Lien retour et reconnexion -->
            <div class="mt-4 text-center">
                <BButton variant="outline-light" size="sm" class="me-2 mb-2" @click="backToLockScreen">
                    <i class="ri-arrow-left-line me-1"></i>Retour
                </BButton>

                <p class="mb-0 text-muted">
                    Ce n'est pas vous ?
                    <a href="#" @click.prevent="login" class="fw-semibold text-primary text-decoration-underline">
                        <span class="align-middle"> Se reconnecter</span>
                    </a>
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.lockscreen-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease-out;
}

.lockscreen-modal {
    border-radius: 20px;
    padding: 2.5rem;
    max-width: 500px;
    width: 95%;
    text-align: center;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    animation: slideUp 0.4s ease-out;
    background: rgba(45, 55, 72, 0.95);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.unlock-form-modal {
    max-width: 520px;
    max-height: 90vh;
    overflow-y: auto;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(40px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Compte à rebours */
.countdown-section {
    margin: 2rem 0;
}

.countdown-display {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
}

.countdown-circle {
    width: 120px;
    height: 120px;
    border: 4px solid #e2e8f0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.countdown-circle::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 4px solid;
    border-color: #405189 transparent transparent transparent;
    border-radius: 50%;
    animation: countdownSpin 1s linear infinite;
}

.countdown-text {
    font-size: 1.5rem;
    font-weight: 700;
    font-family: 'Courier New', monospace;
    color: #405189;
}

@keyframes countdownSpin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.countdown-info {
    margin-top: 1rem;
}

/* Actions */
.modal-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 2.5rem 0 2rem;
}

.unlock-btn {
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
}

.unlock-btn:not(:disabled):hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(64, 81, 137, 0.4);
}

.logout-btn {
    padding: 0.5rem 1rem;
    border-radius: 10px;
    font-weight: 500;
    font-size: 0.9rem;
}

/* Note de sécurité */
.security-note {
    padding-top: 1.5rem;
    border-top: 1px solid #4a5568;
    color: #a0aec0;
}

.security-note i {
    color: #48bb78;
}

/* Alerte de sécurité */
.alert-note {
    background: rgba(245, 101, 101, 0.1);
    border: 1px solid rgba(245, 101, 101, 0.3);
    border-radius: 8px;
    padding: 12px;
    margin-top: 1rem;
}

.alert-note i {
    color: #f06548;
}

.alert-note small {
    color: #f8d7da;
}

/* Styles pour le formulaire */
.position-relative .form-control {
    padding-right: 3rem;
}

.form-control-lg {
    padding: 0.75rem 1rem;
    font-size: 1rem;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.1);
    color: white;
}

.form-control-lg:focus {
    background: rgba(255, 255, 255, 0.15);
    border-color: #405189;
    box-shadow: 0 0 0 0.2rem rgba(64, 81, 137, 0.25);
    color: white;
}

.form-control-lg::placeholder {
    color: rgba(255, 255, 255, 0.6);
}

/* Responsive */
@media (max-width: 576px) {
    .lockscreen-modal {
        padding: 2rem 1.5rem;
        margin: 1rem;
        max-width: 95%;
    }

    .unlock-form-modal {
        max-width: 95%;
    }

    .countdown-circle {
        width: 100px;
        height: 100px;
    }

    .countdown-text {
        font-size: 1.3rem;
    }

    .unlock-btn {
        padding: 0.675rem 1.25rem;
        font-size: 0.95rem;
    }
}

@media (max-width: 480px) {
    .lockscreen-modal {
        padding: 1.5rem;
    }

    .countdown-circle {
        width: 90px;
        height: 90px;
    }

    .countdown-text {
        font-size: 1.2rem;
    }

    .form-control-lg {
        padding: 0.625rem 0.875rem;
        font-size: 0.95rem;
    }
}
</style>

<style>
/* Style global pour SweetAlert - IMPORTANT: sans scoped */
.swal-overlay-high-zindex {
    z-index: 10000 !important;
}

.swal2-container {
    z-index: 10000 !important;
}

.swal2-popup {
    z-index: 10001 !important;
}

/* Désactiver les interactions avec le contenu derrière l'overlay */
body.lockscreen-active {
    overflow: hidden !important;
    pointer-events: none !important;
}

body.lockscreen-active * {
    pointer-events: none !important;
}

body.lockscreen-active .lockscreen-overlay,
body.lockscreen-active .lockscreen-overlay * {
    pointer-events: auto !important;
}
</style>